import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Check, ImagePlus, Trash2 } from 'lucide-react';
import ChannelAvatar from '../ui/ChannelAvatar';
import Tooltip from '../ui/Tooltip';
import { AVATAR_COLORS } from '../chatHelpers';

export const CHANNEL_ICON_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

/**
 * The "Icon" block of the Create channel modal: a live preview that is itself
 * the upload target, the brand colour swatches as a radio group, and an
 * Upload / Replace / Remove row. The file input is `display: none` via its own
 * class — the app has no Tailwind, so `className="hidden"` left the browser's
 * "Choose File / No file chosen" control on screen.
 */
export default function ChannelIconPicker({ name, color, onColor, previewUrl, hasImage, onPickFile, onRemove }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const openPicker = () => fileRef.current?.click();

  return (
    <div className="ncm-icon">
      <button
        type="button"
        className="ncm-icon-preview"
        onClick={openPicker}
        aria-label={t('chat.newChannelModal.changeIcon')}
      >
        <ChannelAvatar channel={{ kind: 'channel', color, avatar_url: previewUrl }} name={name || 'channel'} size={64} />
        <span className="ncm-icon-overlay" aria-hidden>
          <Camera size={18} />
        </span>
      </button>

      <div className="ncm-icon-side">
        <div className="ncm-swatches" role="radiogroup" aria-label={t('chat.newChannelModal.color')}>
          {AVATAR_COLORS.map((c, i) => {
            const on = color === c;
            return (
              <Tooltip key={c} content={t('chat.newChannelModal.colorN', { n: i + 1 })} side="top">
                <button
                  type="button"
                  role="radio"
                  aria-checked={on}
                  className={`ncm-swatch${on ? ' ncm-swatch--on' : ''}`}
                  style={{ '--ncm-swatch': c }}
                  onClick={() => onColor(c)}
                >
                  {on && <Check size={12} strokeWidth={3} aria-hidden />}
                </button>
              </Tooltip>
            );
          })}
        </div>

        <div className="ncm-upload-row">
          <button type="button" className="ncm-btn-soft" onClick={openPicker}>
            <ImagePlus size={14} aria-hidden />
            {hasImage ? t('chat.newChannelModal.replaceImage') : t('chat.newChannelModal.uploadImage')}
          </button>
          {hasImage && (
            <button type="button" className="ncm-btn-ghost" onClick={onRemove}>
              <Trash2 size={13} aria-hidden />
              {t('chat.newChannelModal.removeImage')}
            </button>
          )}
        </div>
        <p className="ncm-hint">{t('chat.newChannelModal.uploadHint')}</p>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={CHANNEL_ICON_ACCEPT}
        className="ncm-file-input"
        data-testid="channel-icon-file"
        onChange={onPickFile}
      />
    </div>
  );
}
